import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone } from "lucide-react";
import { useWhatsAppPopup, WhatsAppContext } from "@/contexts/WhatsAppPopupContext";

const units = [
  { name: "São Francisco do Sul", state: "SC", whatsapp: "5547992069885", phone: "+5547992069885", phoneDisplay: "(47) 99206-9885", highlight: "Sede" },
  { name: "Barra do Sul", state: "SC", whatsapp: "5547992069885", phone: "+5547992069885", phoneDisplay: "(47) 99206-9885", highlight: "Filial" },
  { name: "Balneário Camboriú", state: "SC", whatsapp: "5547991468851", phone: "+5547991468851", phoneDisplay: "(47) 99146-8851", highlight: "Filial" },
  { name: "Canoinhas", state: "SC", whatsapp: "5547991468851", phone: "+5547991468851", phoneDisplay: "(47) 99146-8851", highlight: "Filial" },
];

const messages: Record<WhatsAppContext, string> = {
  generic: "Olá! Vim pelo site e gostaria de informações.",
  escola: "Olá! Vim pelo site e tenho interesse nos cursos da Escola Náutica. Pode me ajudar?",
  despachante: "Olá! Vim pelo site e preciso de ajuda com a documentação da minha embarcação.",
};

const contextLabels: Record<WhatsAppContext, string> = {
  generic: "",
  escola: "Escola Náutica",
  despachante: "Despachante Náutico",
};

const WhatsAppIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
  </svg>
);

const WhatsAppUnitPopup = () => {
  const { state, closePopup } = useWhatsAppPopup();
  const { isOpen, context, mode } = state;

  const isPhone = mode === "phone";

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePopup();
    };
    if (isOpen) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, closePopup]);

  const handleUnitClick = (unit: typeof units[0]) => {
    if (isPhone) {
      window.location.href = `tel:${unit.phone}`;
    } else {
      const text = encodeURIComponent(messages[context]);
      window.open(`https://wa.me/${unit.whatsapp}?text=${text}`, "_blank", "noopener,noreferrer");
    }
    closePopup();
  };

  const subtitle = contextLabels[context];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm"
            onClick={closePopup}
          />

          {/* Modal */}
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="relative w-full max-w-md pointer-events-auto rounded-2xl overflow-hidden"
              style={{
                background: "rgba(15, 23, 42, 0.85)", // Darker background for better contrast
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: "1px solid hsl(43 74% 53% / 0.40)", // Stronger gold border
                boxShadow: "0 0 80px hsl(43 74% 53% / 0.2), 0 25px 50px -12px rgba(0,0,0,0.7)",
              }}
            >
              {/* Header */}
              <div className="px-6 pt-6 pb-4 flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{
                      background: isPhone ? "rgba(52,211,153,0.15)" : "rgba(37,211,102,0.15)",
                      color: isPhone ? "rgb(52,211,153)" : "#25D366",
                    }}
                  >
                    {isPhone ? <Phone size={18} /> : <WhatsAppIcon />}
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-white">
                      {isPhone ? "Qual unidade deseja ligar?" : "Escolha sua unidade"}
                    </h3>
                    {subtitle && (
                      <p className="text-xs text-gold/80 font-medium uppercase tracking-wider mt-0.5">
                        {subtitle}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={closePopup}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all duration-200"
                  aria-label="Fechar"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Divider */}
              <div className="h-px mx-6 bg-gradient-to-r from-transparent via-gold/20 to-transparent" />

              {/* Unit Cards */}
              <div className="p-4 grid grid-cols-2 gap-3">
                {units.map((unit, idx) => (
                  <motion.button
                    key={unit.name}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.07, duration: 0.3 }}
                    onClick={() => handleUnitClick(unit)}
                    className="group relative flex flex-col items-start gap-2 p-4 rounded-xl text-left transition-all duration-300 cursor-pointer"
                    style={{
                      background: "rgba(255,255,255,0.08)", // More opaque card
                      border: "1px solid rgba(255,255,255,0.12)", // More visible border
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.15)";
                      (e.currentTarget as HTMLElement).style.border = "1px solid hsl(43 74% 53% / 0.50)";
                      (e.currentTarget as HTMLElement).style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.08)";
                      (e.currentTarget as HTMLElement).style.border = "1px solid rgba(255,255,255,0.12)";
                      (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
                    }}
                  >
                    {/* Badge */}
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${unit.highlight === "Sede"
                          ? "bg-gold/20 text-gold"
                          : "bg-white/10 text-white/50"
                        }`}
                    >
                      {unit.highlight}
                    </span>

                    {/* Name */}
                    <div>
                      <p className="font-heading text-sm font-semibold text-white leading-tight">
                        {unit.name}
                      </p>
                      <p className="text-xs text-white/40 mt-0.5">{unit.state}</p>
                    </div>

                    {/* Action row */}
                    {isPhone ? (
                      <div className="mt-auto flex flex-col gap-0.5">
                        <span className="text-[11px] font-mono font-semibold text-emerald-400/80 group-hover:text-emerald-400 transition-colors duration-200">
                          {unit.phoneDisplay}
                        </span>
                        <div className="flex items-center gap-1 text-emerald-400/60 group-hover:text-emerald-400 transition-colors duration-200">
                          <Phone size={11} />
                          <span className="text-[10px] font-medium">Ligar agora</span>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-auto flex items-center gap-1.5 text-[#25D366]/70 group-hover:text-[#25D366] transition-colors duration-200">
                        <WhatsAppIcon />
                        <span className="text-xs font-medium">Conversar</span>
                      </div>
                    )}
                  </motion.button>
                ))}
              </div>

              {/* Footer */}
              <p className="text-center text-[11px] text-white/25 pb-4">
                {isPhone
                  ? "Atendimento de segunda a sábado · Horário comercial"
                  : "Resposta em menos de 5 minutos · Atendimento humano"}
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppUnitPopup;
